import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { BLOG_POSTS } from "@/data/blogPosts.ext";
import { COMPANY } from "@/data/site";
import { Calendar, Clock, Phone, ArrowRight } from "lucide-react";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: COMPANY.name },
    publisher: {
      "@type": "Organization",
      name: COMPANY.name,
      logo: {
        "@type": "ImageObject",
        url:
          "https://customer-assets.emergentagent.com/job_premium-moving-wy/artifacts/63dddnb5_IMG_0277.webp",
      },
    },
    description: post.metaDesc,
    mainEntityOfPage: `https://${COMPANY.website}/blog/${post.slug}`,
  };

  return (
    <>
      <SEO
        title={post.seoTitle}
        description={post.metaDesc}
        path={`/blog/${post.slug}`}
        extraSchema={articleSchema}
      />

      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[var(--ct-primary)]/85" />
        </div>
        <div className="section-container text-white max-w-4xl">
          <Link to="/blog" className="text-sm text-white/70 hover:text-white">
            ← Back to Blog
          </Link>
          <div className="mt-6 flex items-center gap-3 text-xs">
            <span className="px-3 py-1 rounded-full bg-[var(--ct-secondary)] text-[var(--ct-primary)] font-semibold uppercase tracking-widest">
              {post.category}
            </span>
            <span className="flex items-center gap-1 text-white/80">
              <Calendar className="w-3 h-3" /> {formatDate(post.date)}
            </span>
            <span className="flex items-center gap-1 text-white/80">
              <Clock className="w-3 h-3" /> {post.readMin} min read
            </span>
          </div>
          <h1
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            style={{ fontFamily: "Outfit" }}
          >
            {post.title}
          </h1>
          <p className="mt-5 text-lg text-white/85 max-w-3xl leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 bg-white">
        <article className="section-container max-w-3xl">
          <div className="space-y-5">
            {post.sections.map((s, i) => {
              if (s.type === "h2") {
                return (
                  <h2
                    key={i}
                    className="text-2xl md:text-3xl font-semibold text-[var(--ct-primary)] tracking-tight pt-6"
                    style={{ fontFamily: "Outfit" }}
                  >
                    {s.text}
                  </h2>
                );
              }
              if (s.type === "list") {
                return (
                  <ul key={i} className="space-y-2 pl-1">
                    {s.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3 text-[var(--ct-text)] leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--ct-secondary)] flex-shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={i} className="text-[var(--ct-text)] leading-relaxed text-[17px]">
                  {s.text}
                </p>
              );
            })}
          </div>

          {/* CTA box */}
          <div className="mt-12 p-8 rounded-3xl bg-[var(--ct-primary)] text-white">
            <h3
              className="text-2xl font-semibold"
              style={{ fontFamily: "Outfit" }}
            >
              Ready for a stress-free move?
            </h3>
            <p className="mt-2 text-white/80">
              Free quotes from Wyoming's most-trusted movers — Casper, Cheyenne, Laramie & beyond.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/#quote" className="btn-primary">
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneTel}`}
                className="btn-outline !text-white !border-white hover:!bg-white hover:!text-[var(--ct-primary)]"
              >
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
            </div>
          </div>
        </article>
      </section>

      {/* Related */}
      <section className="py-16 bg-[var(--ct-bg)]">
        <div className="section-container">
          <div className="label-xs text-[var(--ct-primary)] mb-3">More From the Blog</div>
          <h2
            className="text-2xl md:text-3xl font-semibold text-[var(--ct-primary)] tracking-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Keep reading.
          </h2>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-[var(--ct-border)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={r.image}
                    alt={r.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ct-secondary)] font-semibold">
                    {r.category}
                  </div>
                  <h3
                    className="mt-2 text-lg font-semibold text-[var(--ct-primary)] leading-snug group-hover:text-[var(--ct-secondary)] transition"
                    style={{ fontFamily: "Outfit" }}
                  >
                    {r.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPostPage;
